// src/components/AddEventPage.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, storage, functions } from '../firebase';
import { doc, collection } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faCalendarAlt,
    faPlus,
    faCheck,
    faExclamationCircle,
    faAlignLeft,
    faClock,
    faRepeat,
    faCamera,
    faTimes
} from '@fortawesome/free-solid-svg-icons';

import './FormPage.css';

function AddEventPage() {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventTime, setEventTime] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isRecurring, setIsRecurring] = useState(false);
    const [frequency, setFrequency] = useState('weekly');
    const [monthlyType, setMonthlyType] = useState('dayOfMonth');
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const navigate = useNavigate();
    const { currentUser, isAdmin, userOrgId } = useAuth();

    const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB

    // Get minimum date (today)
    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    // Recurrence helpers
    const getDayOfWeek = () => {
        if (!eventDate) return 0;
        return new Date(eventDate + 'T12:00').getDay(); // 0=Sun..6=Sat
    };

    const getWeekOfMonth = () => {
        if (!eventDate) return 1;
        const d = new Date(eventDate + 'T12:00');
        return Math.floor((d.getDate() - 1) / 7) + 1;
    };

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const ordinals = ['', 'first', 'second', 'third', 'fourth'];

    const getMonthlyDayOfMonthLabel = () => {
        if (!eventDate) return '';
        const day = new Date(eventDate + 'T12:00').getDate();
        const suffix = (day >= 11 && day <= 13) ? 'th' : ['th','st','nd','rd','th','th','th','th','th','th'][day % 10];
        return `On the ${day}${suffix}`;
    };

    const getMonthlyDayOfWeekLabel = () => {
        if (!eventDate) return '';
        const week = getWeekOfMonth();
        const dayName = dayNames[getDayOfWeek()];
        return `On the ${ordinals[Math.min(week, 4)]} ${dayName}`;
    };

    const getRecurrenceSummary = () => {
        if (!eventDate) return '';
        const dayName = dayNames[getDayOfWeek()];
        switch (frequency) {
            case 'weekly': return `Every week on ${dayName}`;
            case 'biweekly': return `Every 2 weeks on ${dayName}`;
            case 'monthly':
                return monthlyType === 'dayOfWeek' ? getMonthlyDayOfWeekLabel() : getMonthlyDayOfMonthLabel();
            case 'yearly': {
                const d = new Date(eventDate + 'T12:00');
                return `Yearly on ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
            }
            default: return '';
        }
    };

    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file.');
            return;
        }
        if (file.size > MAX_PHOTO_SIZE) {
            setError('Photo must be under 10MB.');
            return;
        }
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
        setError(null);
    };

    const handlePhotoRemove = () => {
        setPhotoFile(null);
        if (photoPreview) {
            URL.revokeObjectURL(photoPreview);
            setPhotoPreview(null);
        }
    };

    // Cleanup object URL on unmount
    React.useEffect(() => {
        return () => {
            if (photoPreview) URL.revokeObjectURL(photoPreview);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sanitizeFilename = (name) => name.replace(/[^a-zA-Z0-9._-]/g, '_');

    const uploadEventPhoto = async (file, orgId, docId, isSeries) => {
        const folder = isSeries ? 'eventSeries' : 'event';
        const storagePath = `${orgId}/${folder}/${docId}/${sanitizeFilename(file.name)}`;
        const storageRef = ref(storage, storagePath);
        return new Promise((resolve, reject) => {
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                    setUploadProgress(progress);
                },
                (error) => reject(error),
                () => resolve(storagePath)
            );
        });
    };

    const VALID_FREQUENCIES = ['weekly', 'biweekly', 'monthly', 'yearly'];
    const VALID_MONTHLY_TYPES = ['dayOfMonth', 'dayOfWeek'];

    const validateForm = () => {
        setError(null);
        if (!title.trim()) {
            setError("Event title cannot be empty.");
            return false;
        }
        if (!eventDate) {
            setError("Please select an event date.");
            return false;
        }
        if (!eventTime) {
            setError("Please select an event time.");
            return false;
        }
        // Validate date is in the future
        const selectedDateTime = new Date(`${eventDate}T${eventTime}`);
        if (selectedDateTime < new Date()) {
            setError("Event date and time must be in the future.");
            return false;
        }
        // Validate recurrence fields
        if (isRecurring) {
            if (!VALID_FREQUENCIES.includes(frequency)) {
                setError("Invalid recurrence frequency.");
                return false;
            }
            const dow = getDayOfWeek();
            if (dow < 0 || dow > 6) {
                setError("Invalid day of week for recurrence.");
                return false;
            }
            if (frequency === 'monthly') {
                if (!VALID_MONTHLY_TYPES.includes(monthlyType)) {
                    setError("Invalid monthly recurrence type.");
                    return false;
                }
                if (monthlyType === 'dayOfWeek') {
                    const wom = getWeekOfMonth();
                    if (wom < 1 || wom > 4) {
                        setError("Invalid week of month for recurrence.");
                        return false;
                    }
                }
            }
            if (!/^\d{2}:\d{2}$/.test(eventTime)) {
                setError("Invalid time format for recurrence.");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm() || !currentUser || !isAdmin || !userOrgId) {
            if (!currentUser) setError("You must be logged in to add an event.");
            if (!isAdmin) setError("You must be an admin to add events.");
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            // Combine date and time into a single ISO string
            const dateTimeString = `${eventDate}T${eventTime}`;
            const isoString = new Date(dateTimeString).toISOString();

            // Pre-generate document ID for direct photo upload
            const preGeneratedId = doc(collection(db, isRecurring ? 'eventSeries' : 'events')).id;

            // Upload photo if selected
            let photoPath = null;
            if (photoFile) {
                try {
                    photoPath = await uploadEventPhoto(photoFile, userOrgId, preGeneratedId, isRecurring);
                } catch (uploadErr) {
                    console.error("Error uploading photo:", uploadErr);
                    setError("Failed to upload photo. Please try again.");
                    setIsLoading(false);
                    return;
                }
            }

            const createEventCallable = httpsCallable(functions, 'createEvent');
            const payload = {
                title: title.trim(),
                description: description.trim(),
                dateTime: isoString,
                eventId: preGeneratedId,
            };

            if (photoPath) {
                payload.photoPath = photoPath;
            }

            if (isRecurring) {
                payload.recurrence = {
                    frequency,
                    dayOfWeek: getDayOfWeek(),
                    monthlyType: frequency === 'monthly' ? monthlyType : null,
                    weekOfMonth: (frequency === 'monthly' && monthlyType === 'dayOfWeek') ? getWeekOfMonth() : null,
                    startDate: isoString,
                    time: eventTime,
                };
            }

            await createEventCallable(payload);

            const createdTitle = title.trim();
            const wasRecurring = isRecurring;
            setSuccessMessage(isRecurring
                ? "Recurring event series created! Events will appear shortly."
                : "Event created successfully!");
            setTitle('');
            setDescription('');
            setEventDate('');
            setEventTime('');

            setTimeout(() => {
                navigate('/events', {
                    state: {
                        message: `Event '${createdTitle}' created.`,
                        recurringCreated: wasRecurring
                    }
                });
            }, 1500);
        } catch (err) {
            console.error("Error creating event:", err);
            setError("Failed to create event. Please try again.");
        } finally {
            if (!successMessage) {
                setIsLoading(false);
            }
        }
    };

    // Redirect non-admins
    if (!isAdmin) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/events" className="form-back-btn" title="Back to Events">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Access Denied</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-card">
                        <div className="form-card-body">
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">
                                    Only administrators can create events.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const formInputsDisabled = isLoading || !!successMessage;

    return (
        <div className="form-page">
            {/* Page Header */}
            <div className="form-page-header">
                <div className="container">
                    <div className="form-page-header-content">
                        <div className="form-page-header-left">
                            <Link to="/events" className="form-back-btn" title="Back to Events">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="form-page-title">Create Event</h1>
                                <p className="form-page-subtitle">Add a new event to your organization</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Form Card */}
                <div className="form-card">
                    <div className="form-card-body">
                        {/* Error Alert */}
                        {error && (
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">{error}</div>
                            </div>
                        )}

                        {/* Success Alert */}
                        {successMessage && (
                            <div className="form-alert form-alert-success">
                                <FontAwesomeIcon icon={faCheck} className="form-alert-icon" />
                                <div className="form-alert-content">{successMessage}</div>
                            </div>
                        )}

                        {!successMessage && (
                            <form onSubmit={handleSubmit} noValidate>
                                {/* Event Info Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '0.5rem' }} />
                                        Event Information
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Event Title <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-field-input"
                                            placeholder="Enter event title"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            disabled={formInputsDisabled}
                                            maxLength={200}
                                            autoFocus
                                        />
                                    </div>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            <FontAwesomeIcon icon={faAlignLeft} style={{ marginRight: '0.5rem' }} />
                                            Description
                                        </label>
                                        <textarea
                                            className="form-field-input form-field-textarea"
                                            placeholder="Enter event description (optional)"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            disabled={formInputsDisabled}
                                            rows={4}
                                            maxLength={2000}
                                        />
                                    </div>

                                    {/* Photo Upload */}
                                    <div className="form-field">
                                        <label className="form-field-label">
                                            <FontAwesomeIcon icon={faCamera} style={{ marginRight: '0.5rem' }} />
                                            Event Photo
                                        </label>
                                        {photoPreview ? (
                                            <div className="event-photo-preview-container">
                                                <img src={photoPreview} alt="Event preview" className="event-photo-preview" />
                                                <button
                                                    type="button"
                                                    className="event-photo-remove-btn"
                                                    onClick={handlePhotoRemove}
                                                    disabled={formInputsDisabled}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="event-photo-upload-area" style={{ cursor: formInputsDisabled ? 'default' : 'pointer' }}>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handlePhotoSelect}
                                                    disabled={formInputsDisabled}
                                                    style={{ display: 'none' }}
                                                />
                                                <FontAwesomeIcon icon={faCamera} style={{ fontSize: '1.5rem', color: '#94a3b8' }} />
                                                <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Click to add a photo (max 10MB)</span>
                                            </label>
                                        )}
                                        {uploadProgress > 0 && uploadProgress < 100 && (
                                            <div className="event-photo-progress">
                                                <div className="event-photo-progress-bar" style={{ width: `${uploadProgress}%` }} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Date & Time Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.5rem' }} />
                                        Date & Time
                                    </h3>

                                    <div className="form-row">
                                        <div className="form-field form-field-half">
                                            <label className="form-field-label">
                                                Date <span className="required">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="form-field-input"
                                                value={eventDate}
                                                onChange={(e) => setEventDate(e.target.value)}
                                                disabled={formInputsDisabled}
                                                min={getMinDate()}
                                            />
                                        </div>
                                        <div className="form-field form-field-half">
                                            <label className="form-field-label">
                                                Time <span className="required">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                className="form-field-input"
                                                value={eventTime}
                                                onChange={(e) => setEventTime(e.target.value)}
                                                disabled={formInputsDisabled}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Recurrence Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faRepeat} style={{ marginRight: '0.5rem' }} />
                                        Repeat
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={isRecurring}
                                                onChange={(e) => setIsRecurring(e.target.checked)}
                                                disabled={formInputsDisabled}
                                                style={{ width: '18px', height: '18px', accentColor: '#1e3a5f' }}
                                            />
                                            Make this a recurring event
                                        </label>
                                    </div>

                                    {isRecurring && (
                                        <>
                                            <div className="form-field">
                                                <label className="form-field-label">Frequency</label>
                                                <select
                                                    className="form-field-input"
                                                    value={frequency}
                                                    onChange={(e) => setFrequency(e.target.value)}
                                                    disabled={formInputsDisabled}
                                                >
                                                    <option value="weekly">Weekly</option>
                                                    <option value="biweekly">Every 2 Weeks</option>
                                                    <option value="monthly">Monthly</option>
                                                    <option value="yearly">Yearly</option>
                                                </select>
                                            </div>

                                            {frequency === 'monthly' && eventDate && (
                                                <div className="form-field">
                                                    <label className="form-field-label">Monthly Pattern</label>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                            <input
                                                                type="radio"
                                                                name="monthlyType"
                                                                value="dayOfMonth"
                                                                checked={monthlyType === 'dayOfMonth'}
                                                                onChange={(e) => setMonthlyType(e.target.value)}
                                                                disabled={formInputsDisabled}
                                                            />
                                                            {getMonthlyDayOfMonthLabel()}
                                                        </label>
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                            <input
                                                                type="radio"
                                                                name="monthlyType"
                                                                value="dayOfWeek"
                                                                checked={monthlyType === 'dayOfWeek'}
                                                                onChange={(e) => setMonthlyType(e.target.value)}
                                                                disabled={formInputsDisabled}
                                                            />
                                                            {getMonthlyDayOfWeekLabel()}
                                                        </label>
                                                    </div>
                                                </div>
                                            )}

                                            {eventDate && (
                                                <div style={{
                                                    background: '#eff6ff',
                                                    border: '1px solid #bfdbfe',
                                                    borderRadius: '8px',
                                                    padding: '0.75rem 1rem',
                                                    fontSize: '0.9rem',
                                                    color: '#1e40af',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}>
                                                    <FontAwesomeIcon icon={faRepeat} />
                                                    {getRecurrenceSummary()}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <div className="form-actions">
                                    <button
                                        type="submit"
                                        className="form-submit-btn form-submit-btn-success"
                                        disabled={formInputsDisabled}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" />
                                                {isRecurring ? 'Creating Recurring Event...' : 'Creating Event...'}
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faPlus} />
                                                {isRecurring ? 'Create Recurring Event' : 'Create Event'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}

                        {successMessage && (
                            <div className="form-actions" style={{ borderTop: 'none', paddingTop: 0 }}>
                                <button
                                    type="button"
                                    className="form-submit-btn"
                                    onClick={() => navigate('/events')}
                                >
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                    Go to Events
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddEventPage;
